import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../RootReducer";
import { WithdrawPermitted } from "../http/protocol";
import { ContextStatus, WithdrawPhase } from "../payments/PaymentsReducer";
import ChooseMethod from "./deposit/ChooseMethod";
import "./Withdraw.scss";
import { fetchBalance, setActiveCurrency } from "../user/UserActions";
import { t } from "../i18n";
import { showCashier } from "../payments/DepositActions";
import { getWithdrawMessage } from "../selectors/payments-selectors";
import { getActiveCurrency } from "../selectors/user-selectors";
import { BrandButton, BrandLink } from "./buttons/buttons";
import { reset } from "../payments/PaymentsActions";
import { Spinner } from "react-bootstrap";
import ProfileMetamapComponent from "../profilepage/ProfileMetamapComponent";
import { PaymentLoading } from "../onboardingpage/PaymentLoading";
import { prettyUrl } from "../utils/UrlUtils";
import { PaymentMethodBackButton } from "../onboardingpage/PaymentMethodBackButton";
import CryptoWithdrawMethods from "../onboardingpage/CryptoMethods/CryptoWithdrawMethods";
import ChangeIcon from "../utils/changeIcon";
import ChangeCurrency from "../onboardingpage/ChangeCurrency";
import WithdrawProviderSpecific from "../onboardingpage/WithdrawProviderSpecific";

const Withdraw = () => {
  const dispatch = useDispatch();
  const [showCurrency, setShowCurrency] = useState(false);
  const withdraw = useSelector((state: AppState) => state.payments.withdraw);
  const settings = useSelector((state: AppState) => state.settings.settings);
  const user = useSelector((state: AppState) => state.user.user);
  const withdrawMessage = useSelector((state: AppState) => getWithdrawMessage(state));
  const activeSiteCurrency = useSelector((state: AppState) => getActiveCurrency(state));

  useEffect(() => {
    handleForceWithdrawCurrency();
  }, [settings]);

  const handleForceWithdrawCurrency = () => {
    if (settings && settings.forceWithdrawCurrency && settings.forceWithdrawCurrency !== "") {
      dispatch(setActiveCurrency(settings.forceWithdrawCurrency) as any);
    }
  };

  const handleClickOk = () => {
    dispatch(fetchBalance() as any);
    dispatch(showCashier(false) as any);
  };

  const handleClickChangeCurrency = () => {
    setShowCurrency(!showCurrency);
  };

  if (!withdraw || !activeSiteCurrency?.code || !withdraw.methods) {
    return <PaymentLoading />;
  }

  const phase = withdraw.phase;
  const provider = withdraw.activeMethod?.provider;
  const code = withdraw.activeMethod?.code;
  const method = withdraw.activeMethod?.name;
  const wcStatus = withdraw?.withdrawContextStatus;
  const permitted = withdraw?.withdrawContext?.permitted;
  const isKycRequired = withdraw.activeMethod?.requireKycOnWithdraw;
  const isUserKycVerified = user?.kycVerified;
  const isKycRequirementsNotMet = isKycRequired && !isUserKycVerified;
  const isCryptoCurrency = activeSiteCurrency?.cryptoCurrency;
  const onlyOneMethod = withdraw.methods?.[withdraw?.activeCurrency ?? ""]?.length === 1;
  const userHasChosenMethod = withdraw.activeMethod || withdraw.group;
  const hasWithdrawError = wcStatus === ContextStatus.ERROR;
  const hasMultipleCurrencies = user?.currencies?.length && user.currencies.length > 1;
  const isChangeCurrencyAllowed = phase !== WithdrawPhase.ERROR && phase !== WithdrawPhase.CONFIRM_WITHDRAW && phase !== WithdrawPhase.WITHDRAW_RESPONSE;

  return (
    <div className={`cashier-inner-container withdraw content-wrapper ${method ? prettyUrl(method) : ""}`}>
      <div className={"paymentSelectorButton"}>
        {!showCurrency && (userHasChosenMethod || hasWithdrawError) && !onlyOneMethod && !isCryptoCurrency && (
          <PaymentMethodBackButton onClick={() => dispatch(reset(activeSiteCurrency.code) as any)}>
            {(!hasWithdrawError || withdraw.group) &&
              t(
                `payments.method.${withdraw.activeMethod?.code}.name`,
                undefined,
                withdraw.group ? t(`payments.method.${withdraw.group}.name`) || withdraw.group : withdraw.activeMethod?.name,
              )}
            {hasWithdrawError && !withdraw.group && t(`payments.show-all`)}
          </PaymentMethodBackButton>
        )}
      </div>
      {!isKycRequirementsNotMet &&
        !showCurrency &&
        phase === WithdrawPhase.CHOOSE_METHOD &&
        !withdrawMessage &&
        !withdraw.activeMethod &&
        !withdraw.group &&
        !isCryptoCurrency && (
          <div className="withdrawWrapper">
            {wcStatus === ContextStatus.OK && <ChooseMethod />}

            {wcStatus === ContextStatus.IN_PROGRESS && (
              <div className="chooseMethod">
                <div className="wrapper">
                  <div style={{ width: "100%", padding: "50px", textAlign: "center" }}>
                    <Spinner animation={"border"} />
                  </div>
                </div>
              </div>
            )}

            {wcStatus === ContextStatus.ERROR && (
              <div className="chooseMethod">
                <div className="wrapper">
                  <div className={"col-12"}>
                    {permitted !== WithdrawPermitted.DEPOSITS_WAITING_TO_CLEAR && (
                      <>
                        <div className={"alert alert-danger"}>{t(`withdraw.context-error.${permitted ? permitted : "UNKNOWN_ERROR"}`)}</div>
                        {permitted === WithdrawPermitted.BONUS_ACTIVE && (
                          <BrandLink to={"/account/bonus"} onClick={() => dispatch(showCashier(false) as any)} className={"btn btn-primary full-width"}>
                            {t("withdraw.go-to-bonuses")}
                          </BrandLink>
                        )}
                        {permitted !== WithdrawPermitted.BONUS_ACTIVE && (
                          <BrandButton onClick={() => dispatch(reset(activeSiteCurrency.code) as any)} className={"btn btn-primary full-width"}>
                            {t("forms.screenName.back")}
                          </BrandButton>
                        )}
                      </>
                    )}
                    {permitted === WithdrawPermitted.DEPOSITS_WAITING_TO_CLEAR && (
                      <div className={"col-12"}>
                        <div className={"alert alert-danger"}>{t("withdraw.waiting-for-deposit")}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      {!showCurrency && !withdrawMessage && (
        <>
          {withdraw.group && (
            <CryptoWithdrawMethods
              isKycRequirementsNotMet={isKycRequirementsNotMet}
              phase={phase}
              groupName={withdraw.group}
              handleClickOk={handleClickOk}
              hideCashier={() => dispatch(showCashier(false) as any)}
            />
          )}
          {isCryptoCurrency && !withdraw.group && (
            <CryptoWithdrawMethods
              isKycRequirementsNotMet={isKycRequirementsNotMet}
              phase={phase}
              handleClickOk={handleClickOk}
              hideCashier={() => dispatch(showCashier(false) as any)}
            />
          )}
        </>
      )}

      {!isKycRequirementsNotMet && !showCurrency && withdrawMessage && phase !== WithdrawPhase.WITHDRAW_RESPONSE && (
        <div className="withdraw-amount-invalid">
          <div className={"alert alert-info"}>{t("store.bundles.withdrawAmountNotValidText") + " " + withdrawMessage}</div>
        </div>
      )}

      {!isKycRequirementsNotMet && !showCurrency && !withdraw.group && !isCryptoCurrency && (
        <>{!isKycRequirementsNotMet && <WithdrawProviderSpecific provider={provider} withdraw={withdraw} code={code} handleClickOk={handleClickOk} />}</>
      )}

      {showCurrency && <ChangeCurrency onClose={handleClickChangeCurrency} />}

      {isKycRequirementsNotMet && !showCurrency && (
        <>
          <div className={"alert alert-danger"}>{t("payments.withdrawal-kyc-required")}</div>
          <ProfileMetamapComponent />
        </>
      )}
      {!showCurrency && hasMultipleCurrencies && isChangeCurrencyAllowed ? (
        <span className={"change-currency-button"} onClick={handleClickChangeCurrency}>
          <ChangeIcon className="change" />
          {t("payments.changeCurrency")}
        </span>
      ) : null}
    </div>
  );
};

export default Withdraw;
